import { Snackbar } from "@mui/material";
import {Alert, IconButton} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { createContext, useState } from "react";

const SnackbarContext = createContext();

const alert_severity_success = 'success'
const alert_severity_info = 'info'
const alert_severity_warning = 'warning'
const alert_severity_error = 'error'

export const SnackbarProvider = ({children}) => {
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarAlertMessage, setSnackbarAlertMessage] = useState()
    const [snackbarAlertSeverity, setSnackbarAlertSeverity] = useState(alert_severity_success)
    // Optional React node rendered in the Snackbar's action slot (t_892abb39:
    // the post-restore confirmation carries an Undo). Every existing caller
    // passes no action and gets the old behavior unchanged.
    const [snackbarAction, setSnackbarAction] = useState(null);

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setOpenSnackbar(false);
        setSnackbarAction(null);
    };

    
    const handleSnackbarOpen = () => {
        setOpenSnackbar(true);
    };

    const _openSnackbar = (message, severity, action = null) => {
        setSnackbarAlertMessage(message)
        setSnackbarAlertSeverity(severity)
        setSnackbarAction(action)
        handleSnackbarOpen(true)
    }

    const openSnackbarError = (message) => {
        _openSnackbar(message, alert_severity_error)
    }

    const openSnackbarWarning = (message) => {
        _openSnackbar(message, alert_severity_warning)
    }

    const openSnackbarInfo = (message) => {
        _openSnackbar(message, alert_severity_info)
    }

    const openSnackbarSuccess = (message, action = null) => {
        _openSnackbar(message, alert_severity_success, action)
    }

    return (
        <SnackbarContext.Provider value= {{openSnackbarError, openSnackbarWarning, openSnackbarInfo, openSnackbarSuccess}}>
            <Snackbar
                open={openSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                // A confirmation the user can act on must not vanish under
                // their cursor: with an action present the snackbar persists
                // until it is dismissed (the close control rendered beside
                // the action below) or acted on — the Material guidance for
                // actionable snackbars. Without an action this is the old
                // 6s toast, byte-for-byte.
                autoHideDuration={snackbarAction ? null : 6000}
                onClose={handleSnackbarClose}
                // No `action` prop here on purpose. MUI's Snackbar renders
                // `children || <SnackbarContent message action />`
                // (node_modules/@mui/material/node/Snackbar/Snackbar.js:177),
                // so with a custom child — the Alert below — the Snackbar's own
                // `action` is only read inside that fallback branch and never
                // reaches the DOM. Passing it here looked right in tests (the
                // mock spread it onto a div) and did nothing in the browser:
                // Alex clicked Restore and got no Undo. The action belongs on
                // the Alert, which does support one.
            >
                <Alert
                    onClose={handleSnackbarClose}
                    // With an action, MUI's Alert renders ONLY the action in
                    // its AlertAction slot — the built-in close control is
                    // `action == null && onClose`
                    // (node_modules/@mui/material/Alert/Alert.js:210-214) —
                    // so the actionable toast had no close control at all
                    // (PR #1243 review item 5). Render the action plus a
                    // close beside it; AlertAction is already MUI's flex row
                    // for this slot (:123-133), so a fragment is all it
                    // takes. The control mirrors MUI's own built-in
                    // (size small, color inherit, CloseIcon fontSize small,
                    // title "Close" + aria-label "Close notification" —
                    // Alert.js:219-220 sets both — and color inherit is what
                    // keeps the X readable on the filled alert surface) and
                    // dismissal goes through
                    // handleSnackbarClose, which closes AND clears the
                    // action. Without an action: `undefined`, exactly as
                    // before — MUI's built-in close on the old 6s toast.
                    action={snackbarAction ? (
                        <>
                            {snackbarAction}
                            <IconButton
                                size="small"
                                color="inherit"
                                title="Close"
                                aria-label="Close notification"
                                onClick={handleSnackbarClose}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </>
                    ) : undefined}
                    severity={snackbarAlertSeverity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbarAlertMessage}
                </Alert>
            </Snackbar>

            {children}
        </SnackbarContext.Provider>
    )

}

export default SnackbarContext;